import { Settings } from 'lucide-react'
import { cn } from '@teach/ui'
import { useAppDispatch } from '@/store/hooks'
import { selectBlock, setBlockSheetOpen } from '@/features/editor/editorSlice'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { Lesson } from '@/types/lesson'
import { useMessages } from '@/i18n'
import { enterLessonSettings } from './useLessonLastBlock'
import { messages } from './messages'

interface Props {
  lesson: Omit<Lesson, 'blocks'>
  lessonIndex: number
  contentLang: ContentLang
  isSelected: boolean
  /** Выбран сам урок (открыты его настройки, экран не выбран) — плашка ярче. */
  isOpen?: boolean
  onExpand: () => void
}

export const LessonPill = ({
  lesson,
  lessonIndex,
  contentLang,
  isSelected,
  isOpen = false,
  onExpand,
}: Props) => {
  // Показ, не правка: следует contentLang — у английского урока name пуст,
  // заголовок лежит в nameEn (getLocalizedDisplay сам делает симметричный
  // фолбэк, когда слот текущего языка пуст).
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  // Настройки урока — шестерёнкой внутри выбранной плашки, справа от названия; на устройстве
  // с мышью она видна при наведении. Снятый экран — панель показывает урок (`BlockPanel`: нет черновика → урок).
  // Шестерёнка соседнего урока (только на ПК) сразу входит в него и открывает настройки.
  const openSettings = () => {
    if (!isSelected) {
      enterLessonSettings(lesson.id)
      onExpand()
    }
    dispatch(selectBlock(null))
    dispatch(setBlockSheetOpen(true))
  }
  const name = getLocalizedDisplay(lesson, 'name', contentLang, false)
  // Без названия — серым «Урок N»: подсказка, а не название (в данные не пишется).
  const title = name || m.lessonNumber(lessonIndex + 1)
  return (
    <div className="group/lesson relative shrink-0">
      {/* Удаления на плашке нет: оно внизу вкладки «Инфо» панели урока
          (`LessonDeleteButton`) — корзинка от наведения удаляла урок мимоходом. */}
      {/* Плашка урока: номер в кружке и название (длинное — троеточием, полное в подсказке). */}
      <button
        type="button"
        data-selected={isSelected}
        title={title}
        aria-label={title}
        onClick={onExpand}
        className={cn(
          // Наведение — плашка чуть увеличивается, как миниатюры блоков приподнимаются. Наведение
          // считается по всей группе: на шестерёнке плашка остаётся увеличенной.
          'flex h-10 short:h-8 max-w-[190px] items-center gap-2 rounded-full py-0 pl-1.5 pr-4 text-sm transition-all group-hover/lesson:scale-105',
          // Место под шестерёнку: на устройстве с мышью оно появляется при наведении и сжимает
          // название. Открыты настройки урока — шестерёнка на месте и без наведения.
          isOpen
            ? 'pr-8'
            : cn(
                isSelected ? 'pr-8 lg:[@media(hover:hover)]:pr-4' : null,
                'lg:[@media(hover:hover)]:group-hover/lesson:pr-8',
              ),
          // «Открыт сам урок» (его настройки) — ярче и с обводкой только с `lg`: на
          // телефоне плашка просто переключает урок, отдельного состояния не нужно.
          isOpen
            ? 'bg-brand/20 text-foreground lg:bg-brand/45 lg:ring-1 lg:ring-inset lg:ring-brand'
            : isSelected
              ? 'bg-brand/20 text-foreground'
              : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground',
        )}
      >
        <span
          className={cn(
            'flex size-7 short:size-6 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold',
            isSelected ? 'bg-brand text-brand-foreground' : 'bg-foreground/10 text-foreground',
          )}
        >
          {lessonIndex + 1}
        </span>
        {/* Телефон: название — не длиннее ~9 символов, дальше многоточие (полное — в
            подсказке): иначе две-три плашки не помещались в ширину. */}
        <span className={cn('max-w-[9ch] truncate sm:max-w-none', name ? null : 'opacity-60')}>
          {title}
        </span>
      </button>
      <button
        type="button"
        aria-label={m.lessonSettings}
        title={m.lessonSettings}
        onClick={openSettings}
        className={cn(
          'absolute right-1.5 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          // У соседнего урока шестерёнка только на ПК с мышью: на телефоне — лишь у выбранного.
          isSelected ? null : 'hidden lg:[@media(hover:hover)]:flex',
          // Настройки открыты — шестерёнка белая и чуть крупнее.
          isOpen
            ? 'text-white'
            : 'text-foreground/50 hover:text-foreground lg:[@media(hover:hover)]:opacity-0 lg:[@media(hover:hover)]:focus-visible:opacity-100 lg:[@media(hover:hover)]:group-hover/lesson:opacity-100',
        )}
      >
        <Settings className={isOpen ? 'size-[18px]' : 'size-4'} />
      </button>
    </div>
  )
}
